import React, { useState } from 'react';
import Modal from 'react-responsive-modal';
import { 
  formatErrorForDisplay, 
  getErrorIcon, 
  getErrorColor, 
  isRetryableError, 
  getSuggestedAction 
} from '../../Components/functions/ErrorHandler';

/**
 * Transaction Error Modal Component
 * Displays user-friendly error messages with optional technical details
 */
export const TransactionErrorModal = ({ 
  isOpen, 
  onClose, 
  errorMessage, 
  onRetry = null,
  showTechnicalDetails = false 
}) => {
  const [showTechnical, setShowTechnical] = useState(showTechnicalDetails);
  
  if (!isOpen || !errorMessage) return null;
  
  const errorData = formatErrorForDisplay(errorMessage, showTechnical);
  const icon = getErrorIcon(errorData.category);
  const color = getErrorColor(errorData.category);
  const canRetry = isRetryableError(errorData.category) && onRetry;
  const suggestedAction = getSuggestedAction(errorData.category);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      center
      classNames={{
        modal: "min-w-[400px] max-w-[90vw] sm:min-w-[500px] rounded-xl border border-gray-200 shadow-lg bg-white",
        overlay: "bg-black/50 backdrop-blur-sm"
      }}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
              style={{ backgroundColor: `${color}20`, color: color }}
            >
              {icon}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{errorData.title}</h2>
              <p className="text-sm text-gray-500">Transaction failed</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Main Error Message */}
        <div className="mb-6">
          <div 
            className="p-4 rounded-lg border-l-4"
            style={{ 
              backgroundColor: `${color}10`, 
              borderLeftColor: color 
            }}
          >
            <p className="text-gray-800 leading-relaxed">
              {errorData.message}
            </p>
          </div>
        </div>

        {/* Suggested Action */}
        {suggestedAction && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Suggested Action:</h3>
            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
              {suggestedAction}
            </p>
          </div>
        )}

        {/* Technical Details Toggle */}
        <div className="mb-6">
          <button
            onClick={() => setShowTechnical(!showTechnical)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            <svg 
              className={`w-4 h-4 transition-transform ${showTechnical ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            {showTechnical ? 'Hide' : 'Show'} Technical Details
          </button>
          
          {showTechnical && errorData.technicalDetails && (
            <div className="mt-3 p-4 bg-gray-100 rounded-lg">
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Category:</span>
                  <span className="ml-2 text-gray-600">{errorData.technicalDetails.category}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Technical Message:</span>
                  <span className="ml-2 text-gray-600">{errorData.technicalDetails.technicalMessage}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Original Error:</span>
                  <div className="mt-1 p-2 bg-white rounded border text-xs font-mono text-gray-600 break-all">
                    {errorData.technicalDetails.originalError}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Close
          </button>
          {canRetry && (
            <button
              onClick={() => {
                onClose();
                onRetry();
              }}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

/**
 * Simple Error Toast Component
 * For quick error notifications
 */
export const ErrorToast = ({ errorMessage, onClose }) => {
  const errorData = formatErrorForDisplay(errorMessage);
  const icon = getErrorIcon(errorData.category);
  const color = getErrorColor(errorData.category);

  return (
    <div className="fixed top-4 right-4 z-50 max-w-md">
      <div 
        className="p-4 rounded-lg shadow-lg border-l-4 bg-white"
        style={{ borderLeftColor: color }}
      >
        <div className="flex items-start gap-3">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center text-lg flex-shrink-0"
            style={{ backgroundColor: `${color}20`, color: color }}
          >
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm">Transaction Failed</h3>
            <p className="text-sm text-gray-700 mt-1">{errorData.message}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Inline Error Component
 * For displaying errors within forms or components
 */
export const InlineError = ({ errorMessage, className = "" }) => {
  if (!errorMessage) return null;
  
  const errorData = formatErrorForDisplay(errorMessage);
  const icon = getErrorIcon(errorData.category);
  const color = getErrorColor(errorData.category);

  return (
    <div 
      className={`flex items-start gap-2 p-3 rounded-lg border ${className}`}
      style={{ 
        backgroundColor: `${color}10`, 
        borderColor: `${color}30` 
      }}
    >
      <div 
        className="w-5 h-5 rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-0.5"
        style={{ backgroundColor: `${color}20`, color: color }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800">{errorData.message}</p>
      </div>
    </div>
  );
};
